import { formatCurrencyRange } from '@/lib/formatters'
import { Disclosure } from './Disclosure'
import { PaymentPlansTable } from './PaymentPlansTable'
import { type FinancingOffer, NO_INFORMATION } from './quoteCardTypes'
import { QuoteCardSection } from './quotesAnalytics'

/** The down payment estimate's range, as a share of the card's total price. */
const DOWN_PAYMENT_SHARE = { low: 0.15, high: 0.25 }

interface FinancingSectionProps {
  /** Unset when we have no sourced financing terms for the carrier. */
  financing?: FinancingOffer
  /** A firm quote with no terms points the customer at the financing partners we work with. */
  isQuoted?: boolean
  /** The card's headline price, which the down payment estimate is a share of. */
  totalPremium?: number
}

/** Carrier-specific premium financing content — some carriers have a standard offer, others bespoke. */
export function FinancingSection({ financing, isQuoted, totalPremium }: FinancingSectionProps) {
  if (!financing && isQuoted)
    return (
      <Disclosure section={QuoteCardSection.FINANCING} label="Financing options">
        <div className="flex flex-col gap-2">
          <p className="text-ink-700 text-xs leading-relaxed">
            Cai can connect you to the biggest names in financing for your insurance payments like Imperial
            PFS, First Insurance Funding, Lendio, and more. Ask your insurance broker about down payment
            support when requesting checkout.
          </p>
          {!!totalPremium && (
            <div className="flex flex-col gap-1">
              <p className="text-foreground text-xs leading-relaxed font-medium">
                Estimated down payment:{' '}
                {formatCurrencyRange({
                  low: Math.round(totalPremium * DOWN_PAYMENT_SHARE.low),
                  high: Math.round(totalPremium * DOWN_PAYMENT_SHARE.high),
                })}
              </p>
              <p className="text-ink-400 text-[10px] leading-relaxed">
                Example range of 15% to 25% down followed by monthly payments. Actual terms depend on your
                business.
              </p>
            </div>
          )}
        </div>
      </Disclosure>
    )

  if (!financing)
    return (
      <Disclosure section={QuoteCardSection.FINANCING} label="Financing options" summary={NO_INFORMATION} />
    )

  return (
    <Disclosure
      section={QuoteCardSection.FINANCING}
      label="Financing options"
      summary={financing.isStandard ? undefined : 'Available on request'}
    >
      {/* The carrier's own plans outrank our canned "typically available" text, so they replace it. */}
      {financing.plans?.length ? (
        <div className="flex flex-col gap-3">
          {financing.plansNote && (
            <p className="text-ink-700 text-xs leading-relaxed">{financing.plansNote}</p>
          )}
          <PaymentPlansTable plans={financing.plans} />
          <p className="text-ink-400 text-[10px] leading-relaxed">
            Review your quote document to confirm payment options.
          </p>
        </div>
      ) : (
        <p className="text-ink-700 text-xs leading-relaxed">{financing.description}</p>
      )}
    </Disclosure>
  )
}
